import * as React from 'react';
import { useCallback, useState } from 'react';

import { ENTER_KEY, ESC_KEY } from '../constants';
import {
    findDictionaryEquivalent,
    getComparativeOperatorsDictionary,
    mapTagValueToString,
    onTagEditEnd
} from '../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import { DictionaryItem } from '../typings/common';
import { TagEditorProps } from '../typings/tagCloudTargeting';

type TagInputPasteCallback = (evt: React.ClipboardEvent<HTMLInputElement>) => void;

export function useTagInputPaste(props: TagEditorProps): TagInputPasteCallback {
    const { onPaste, suggestItems } = props;

    return useCallback(
        (evt: React.ClipboardEvent<HTMLInputElement>) => {
            evt.preventDefault();

            if (onPaste) {
                onPaste(
                    evt.clipboardData
                        .getData('Text')
                        .split(/[\t\n]/)
                        .reduce((acc, item) => {
                            const trimmedItem = item.trim();

                            if (trimmedItem === '') {
                                return acc;
                            }

                            if (!suggestItems) {
                                return [...acc, [trimmedItem]];
                            }

                            const dictionaryItem = findDictionaryEquivalent(trimmedItem, suggestItems);

                            if (!dictionaryItem) {
                                return acc;
                            }

                            return [...acc, [dictionaryItem]];
                        }, [])
                );
            }
        },
        [onPaste]
    );
}

type TagInputKeyDownCallback = (evt: React.KeyboardEvent<HTMLInputElement>) => void;

export function useTagInputKeyDown(
    value: string,
    props: TagEditorProps,
    setValue: (value: string) => void
): TagInputKeyDownCallback {
    const { onSubmit, onCancel, onDelete, index } = props;

    return useCallback(
        (evt: React.KeyboardEvent<HTMLInputElement>) => {
            switch (evt.key) {
                case ENTER_KEY:
                    evt.preventDefault();
                    onTagEditEnd(value, props, false, setValue);
                    return;

                case ESC_KEY:
                    onCancel();
                    return;

                default:
                    return;
            }
        },
        [value, setValue, onCancel, onDelete, onSubmit, index]
    );
}

type TagInputBlurCallback = (evt: React.FocusEvent<HTMLInputElement>) => void;

export function useTagInputBlur(
    value: string,
    props: TagEditorProps,
    setValue: (value: string) => void
): TagInputBlurCallback {
    const { onSubmit, onCancel, onBlur, onDelete, index } = props;

    return useCallback(() => {
        onTagEditEnd(value, props, true, setValue);
    }, [value, setValue, onBlur, onSubmit, onDelete, onCancel, index]);
}

interface TagInputHandlers {
    value: string;
    onChange: (value: string) => void;
    onPaste: TagInputPasteCallback;
    onBlur: TagInputBlurCallback;
    onKeyDown: TagInputKeyDownCallback;
}

export function useTagInputHandlers(props: TagEditorProps): TagInputHandlers {
    const { tagValue = '' } = props;
    const [value, setValue] = useState(mapTagValueToString(tagValue));

    const onValuePaste = useTagInputPaste(props);
    const onInputKeyDown = useTagInputKeyDown(value, props, setValue);
    const onInputBlur = useTagInputBlur(value, props, setValue);

    const onChange = useCallback(
        (newValue: string) => {
            setValue(newValue);
        },
        [setValue]
    );

    return {
        value,
        onChange,
        onKeyDown: onInputKeyDown,
        onPaste: onValuePaste,
        onBlur: onInputBlur
    };
}

interface SuggestEditStage {
    editStage: number;
    setEditStage: (stage: number) => void;
    items: DictionaryItem[];
}

export function useSuggestEditStage(suggestItems: DictionaryItem[], value?: string[]): SuggestEditStage {
    const [editStage, setEditStage] = useState(value ? value.length : 0);
    let items: DictionaryItem[];

    switch (editStage) {
        case 0:
            items = suggestItems;
            break;

        case 1:
        case 3:
            items = getComparativeOperatorsDictionary();
            break;

        default:
            items = [];
    }

    return {
        editStage,
        setEditStage,
        items
    };
}
